import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { userAPI, clientSettingsAPI, attendanceAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import EmployeeBulkUpload from './EmployeeBulkUpload';
import {
  Button,
  Card,
  Modal,
  Input,
  Select,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  Badge,
  Pagination,
} from '../../UI';
import SearchInput from '../../UI/SearchInput';
import { SearchStatus, SearchResultsSkeleton } from '../../UI/SearchLoader';
import { UserPlus, Eye, Edit, Trash2, ToggleLeft, ToggleRight, ExternalLink, MapPin, Download, Clock, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, Shield } from 'lucide-react';

const EmployeesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // API hooks — skip auto-fetch; manual effect below controls all fetching
  const { data: usersData, loading: usersLoading, refetch: refetchUsers } = useApi(userAPI.getUsers, [], { skip: true });
  const { data: userStats, loading: statsLoading, refetch: refetchStats } = useApi(userAPI.getStats, []);
  const { data: settingsData } = useApi(clientSettingsAPI.getMySettings, []);
  
  // Office locations from client settings
  const officeLocations = settingsData?.officeLocations || [];
  
  // Role to Department mapping
  const roleDepartmentMap = {
    'employee': 'Operations',
    'marketing': 'Marketing',
    'manager': 'Management',
    'admin': 'Administration',
  };
  
  // Async actions
  const { loading: createLoading, execute: executeCreate } = useAsyncAction();
  const { loading: editLoading, execute: executeEdit } = useAsyncAction();
  const { loading: deleteLoading, execute: executeDelete } = useAsyncAction();
  const { loading: toggleLoading, execute: executeToggle } = useAsyncAction();
  const { loading: exportLoading, execute: executeExport } = useAsyncAction();
  
  const users = usersData?.users || [];
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'all' | 'active' | 'inactive'

  // Today's attendance report state
  const [todayReport, setTodayReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportLocation, setReportLocation] = useState('all');
  const [showReport, setShowReport] = useState(true);
  const [activeReportTab, setActiveReportTab] = useState('present');
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Deactivation form
  const [deactivationForm, setDeactivationForm] = useState({
    reason: '',
    details: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role: '',
    phone: '',
    department: '',
    locationType: 'office', // 'office' for stable, 'field' for traveling employees
    allowAllOfficeLocations: false,
    assignedOfficeLocations: [],
    permissions: { canGeneratePayroll: false, canGenerateLetters: false },
  });

  // Build active filter for API calls
  const activeFilter = activeTab === 'active' ? 'true' : activeTab === 'inactive' ? 'false' : undefined;

  // Single effect: immediate fetch on tab change, debounced on search input
  useEffect(() => {
    const filter = activeTab === 'active' ? 'true' : activeTab === 'inactive' ? 'false' : undefined;
    const params = {};
    if (search) params.search = search;
    if (filter !== undefined) params.active = filter;

    if (search) {
      const timer = setTimeout(() => {
        setSearchLoading(true);
        refetchUsers(params).finally(() => setSearchLoading(false));
      }, 500);
      return () => clearTimeout(timer);
    } else {
      refetchUsers(params);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeTab]);

  // Fetch today's present report
  const fetchTodayReport = async (dateStr, location = 'all') => {
    setReportLoading(true);
    try {
      const params = { date: dateStr };
      if (location && location !== 'all') {
        params.location = location;
      }
      const response = await attendanceAPI.getTodayPresentReport(params);
      setTodayReport(response.data || null);
    } catch (err) {
      console.error('Failed to fetch today report:', err);
      setTodayReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayReport(reportDate, reportLocation);
  }, [reportDate, reportLocation]);

  // Handle export to Excel
  const handleExportReport = async () => {
    try {
      await attendanceAPI.exportTodayAttendanceReport(reportDate, reportLocation);
      setSuccess('Report exported successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to export report: ' + (err.message || 'Unknown error'));
      setTimeout(() => setError(''), 5000);
    }
  };

  // Format time from ISO string to HH:MM AM/PM IST
  const formatTime = (timeStr) => {
    if (!timeStr) return '—';
    const d = new Date(timeStr);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  };

  // Format work duration in minutes to Xh Ym
  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins}m`;
    return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.role) {
      setError('Please select a role');
      return;
    }
    const result = await executeCreate(userAPI.createUser, form);
    if (result.success) {
      setSuccess('Employee created successfully');
      setIsAddModalOpen(false);
      setForm({ name: '', username: '', email: '', password: '', role: '', phone: '', department: '', locationType: 'office', allowAllOfficeLocations: true, assignedOfficeLocations: [], permissions: { canGeneratePayroll: false, canGenerateLetters: false } });
      refetchUsers();
      refetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to create employee');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const result = await executeEdit(userAPI.updateUser, selectedUser._id, form);
    if (result.success) {
      setSuccess('Employee updated successfully');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      refetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to update employee');
    }
  };

  const handleDelete = async () => {
    const result = await executeDelete(userAPI.deleteUser, selectedUser._id);
    if (result.success) {
      setSuccess('Employee deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      refetchUsers();
      refetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to delete employee');
    }
  };

  // Handle toggle status - if activating, do it directly. If deactivating, show modal
  const handleToggleStatus = (emp) => {
    if (emp.active) {
      // Deactivating - show modal for reason
      setSelectedUser(emp);
      setDeactivationForm({
        reason: '',
        details: '',
        date: new Date().toISOString().split('T')[0]
      });
      setIsDeactivateModalOpen(true);
    } else {
      // Reactivating - do it directly
      handleReactivate(emp._id);
    }
  };

  const handleReactivate = async (userId) => {
    const result = await executeToggle(userAPI.toggleUserStatus, userId, {});
    if (result.success) {
      setSuccess('Employee activated successfully');
      refetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to activate employee');
    }
  };

  const handleDeactivate = async () => {
    if (!deactivationForm.reason) {
      setError('Please select a reason for deactivation');
      return;
    }

    const result = await executeToggle(userAPI.toggleUserStatus, selectedUser._id, deactivationForm);
    if (result.success) {
      setSuccess('Employee deactivated successfully');
      setIsDeactivateModalOpen(false);
      setSelectedUser(null);
      refetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to deactivate employee');
    }
  };

  const openEditModal = (emp) => {
    setSelectedUser(emp);
    setForm({
      name: emp.name || '',
      username: emp.username || '',
      email: emp.email || '',
      password: '',
      role: emp.role || 'marketing',
      phone: emp.phone || '',
      department: emp.department || '',
      locationType: emp.locationType || 'office',
      allowAllOfficeLocations: emp.allowAllOfficeLocations ?? true,
      assignedOfficeLocations: emp.assignedOfficeLocations || [],
      permissions: emp.permissions || { canGeneratePayroll: false, canGenerateLetters: false },
    });
    setIsEditModalOpen(true);
  };

  const openViewModal = (emp) => {
    setSelectedUser(emp);
    setIsViewModalOpen(true);
  };

  const openDeleteModal = (emp) => {
    setSelectedUser(emp);
    setIsDeleteModalOpen(true);
  };

  // Handle role change and auto-set department
  const handleRoleChange = (newRole) => {
    setForm({ 
      ...form, 
      role: newRole, 
      department: roleDepartmentMap[newRole] || form.department 
    });
  };

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      await executeExport(userAPI.exportToExcel);
      setSuccess('Employee data exported successfully');
    } catch (err) {
      setError(err.message || 'Failed to export data');
    }
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Employees</h2>
          <p className="text-gray-500">Manage your organization's employees</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="secondary" 
            onClick={handleExportExcel} 
            loading={exportLoading}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsBulkUploadOpen(true)}
            className="flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import Excel
          </Button>
          <Button onClick={() => navigate('/dashboard/employees/add')} className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Add Employee
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Employees</p>
          <p className="text-2xl font-bold text-gray-900">{statsLoading ? '...' : userStats?.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{statsLoading ? '...' : userStats?.activeCount || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Inactive</p>
          <p className="text-2xl font-bold text-red-600">{statsLoading ? '...' : (userStats?.total - userStats?.activeCount) || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Admins</p>
          <p className="text-2xl font-bold text-blue-600">{statsLoading ? '...' : userStats?.byRole?.admin || 0}</p>
        </div>
      </div>

      {/* Today's Attendance Report */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Report Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Today's Attendance Report</h3>
              <p className="text-xs text-gray-500">{todayReport?.dateFormatted || 'Loading...'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Location Filter */}
            {officeLocations && officeLocations.length > 0 && (
              <select
                value={reportLocation}
                onChange={(e) => setReportLocation(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Locations</option>
                {officeLocations.map(loc => (
                  <option key={loc._id} value={loc._id}>{loc.name}</option>
                ))}
              </select>
            )}
            
            {/* Date Picker */}
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            
            {/* Export Button */}
            <button
              onClick={handleExportReport}
              disabled={exportLoading || !todayReport}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Export to Excel"
            >
              {exportLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="hidden sm:inline">Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Export Excel</span>
                </>
              )}
            </button>
            
            {/* Toggle Button */}
            <button
              onClick={() => setShowReport(!showReport)}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {showReport ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
          </div>
        </div>

        {showReport && (
          <>
            {/* Summary Stats Row */}
            {todayReport?.summary && (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-0 border-b border-gray-100">
                <button
                  onClick={() => setActiveReportTab('present')}
                  className={`py-3 text-center transition-colors ${activeReportTab === 'present' ? 'bg-green-50 border-b-2 border-green-500' : 'hover:bg-gray-50'}`}
                >
                  <p className="text-xl font-bold text-green-600">{todayReport.summary.present}</p>
                  <p className="text-xs text-gray-500">Present</p>
                </button>
                <button
                  onClick={() => setActiveReportTab('halfDay')}
                  className={`py-3 text-center transition-colors ${activeReportTab === 'halfDay' ? 'bg-orange-50 border-b-2 border-orange-500' : 'hover:bg-gray-50'}`}
                >
                  <p className="text-xl font-bold text-orange-600">{todayReport.summary.halfDay}</p>
                  <p className="text-xs text-gray-500">Half Day</p>
                </button>
                <button
                  onClick={() => setActiveReportTab('late')}
                  className={`py-3 text-center transition-colors ${activeReportTab === 'late' ? 'bg-yellow-50 border-b-2 border-yellow-500' : 'hover:bg-gray-50'}`}
                >
                  <p className="text-xl font-bold text-yellow-600">{todayReport.summary.late}</p>
                  <p className="text-xs text-gray-500">Late</p>
                </button>
                <button
                  onClick={() => setActiveReportTab('onLeave')}
                  className={`py-3 text-center transition-colors ${activeReportTab === 'onLeave' ? 'bg-blue-50 border-b-2 border-blue-500' : 'hover:bg-gray-50'}`}
                >
                  <p className="text-xl font-bold text-blue-600">{todayReport.summary.onLeave}</p>
                  <p className="text-xs text-gray-500">On Leave</p>
                </button>
                <button
                  onClick={() => setActiveReportTab('absent')}
                  className={`py-3 text-center transition-colors ${activeReportTab === 'absent' ? 'bg-red-50 border-b-2 border-red-500' : 'hover:bg-gray-50'}`}
                >
                  <p className="text-xl font-bold text-red-600">{todayReport.summary.absent}</p>
                  <p className="text-xs text-gray-500">Absent</p>
                </button>
                <div className="py-3 text-center bg-gray-50">
                  <p className="text-xl font-bold text-gray-700">{todayReport.summary.totalActive}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
              </div>
            )}

            {/* Report Table */}
            {reportLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {(() => {
                  const records = todayReport?.[activeReportTab] || [];
                  const tabConfig = {
                    present: { label: 'Present Employees', color: 'green', icon: CheckCircle },
                    halfDay: { label: 'Half Day', color: 'orange', icon: Clock },
                    late: { label: 'Late Arrivals', color: 'yellow', icon: AlertCircle },
                    onLeave: { label: 'On Leave', color: 'blue', icon: Clock },
                    absent: { label: 'Absent / Not Marked', color: 'red', icon: XCircle },
                  };
                  const config = tabConfig[activeReportTab];

                  if (records.length === 0) {
                    return (
                      <div className="text-center py-10 text-gray-500">
                        <config.icon className={`w-10 h-10 mx-auto mb-3 text-${config.color}-300`} />
                        <p className="font-medium">No {config.label.toLowerCase()} for this date</p>
                      </div>
                    );
                  }

                  return (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Punch In</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Punch Out</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remark</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {records.map((record, idx) => {
                          const statusBadge = {
                            present: 'bg-green-100 text-green-700',
                            half_day: 'bg-orange-100 text-orange-700',
                            leave: 'bg-blue-100 text-blue-700',
                            absent: 'bg-red-100 text-red-700',
                            not_marked: 'bg-gray-100 text-gray-600',
                          };
                          const violations = record.violations || [];
                          const remarkParts = [];
                          if (record.notes) remarkParts.push(record.notes);
                          if (record.isLateArrival) remarkParts.push('Late Arrival');
                          if (record.isEarlyDeparture) remarkParts.push('Early Departure');
                          violations.forEach(v => {
                            if (v.description) remarkParts.push(v.description);
                          });
                          const remark = remarkParts.join(' | ') || '—';

                          return (
                            <tr key={record._id || record.employee._id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-medium">
                                    {record.employee.name?.charAt(0)?.toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{record.employee.name}</p>
                                    <p className="text-xs text-gray-500">{record.employee.employeeId || record.employee.username}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-700">{record.employee.department || '—'}</p>
                                <p className="text-xs text-gray-400">{record.employee.designation || ''}</p>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                {record.punchIn ? (
                                  <span className={record.isLateArrival ? 'text-yellow-600 font-medium' : 'text-green-600'}>
                                    {formatTime(record.punchIn)}
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                {record.punchOut ? (
                                  <span className={record.isEarlyDeparture ? 'text-orange-600 font-medium' : ''}>
                                    {formatTime(record.punchOut)}
                                  </span>
                                ) : (
                                  record.punchIn ? <span className="text-blue-500 text-xs">Working...</span> : '—'
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                {formatDuration(record.workDuration)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[record.status] || statusBadge.not_marked}`}>
                                  {record.status === 'not_marked' ? 'Not Marked' : record.status === 'half_day' ? 'Half Day' : record.status?.charAt(0).toUpperCase() + record.status?.slice(1)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px]">
                                <span className="line-clamp-2" title={remark}>{remark}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {[
          { key: 'all',      label: 'All',      count: (userStats?.total || 0) },
          { key: 'active',   label: 'Active',   count: (userStats?.activeCount || 0) },
          { key: 'inactive', label: 'Inactive', count: Math.max(0, (userStats?.total || 0) - (userStats?.activeCount || 0)) },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setSearch(''); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
            <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded text-xs font-semibold ${
              activeTab === tab.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {statsLoading ? '...' : tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <SearchInput
          placeholder="Search employees by name, email, username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          isLoading={searchLoading}
          className="max-w-md"
        />
        <SearchStatus isSearching={searchLoading} resultCount={users.length} searchTerm={search} />
      </div>

      {/* Table */}
      {usersLoading ? (
        <SearchResultsSkeleton rows={5} />
      ) : users.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((emp) => (
                  <tr key={emp._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium">
                          {emp.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{emp.name}</p>
                          <p className="text-sm text-gray-500">@{emp.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">{emp.email || 'N/A'}</p>
                      <p className="text-sm text-gray-500">{emp.phone || 'N/A'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={emp.role === 'admin' ? 'danger' : 'primary'}>
                        {emp.role?.charAt(0).toUpperCase() + emp.role?.slice(1)}
                      </Badge>
                      {emp.department && <p className="text-xs text-gray-500 mt-1">{emp.department}</p>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Badge variant={emp.active ? 'success' : 'secondary'}>
                          {emp.active ? 'Active' : 'Inactive'}
                        </Badge>
                        {!emp.active && emp.inactivation?.reason && (
                          <span className="text-xs text-red-600 capitalize">
                            ({emp.inactivation.reason})
                          </span>
                        )}
                        {emp._id !== user._id && (
                          <button
                            onClick={() => handleToggleStatus(emp)}
                            disabled={toggleLoading}
                            className="p-1 rounded hover:bg-gray-100"
                            title={emp.active ? 'Deactivate' : 'Activate'}
                          >
                            {emp.active ? (
                              <ToggleRight className="w-5 h-5 text-green-600" />
                            ) : (
                              <ToggleLeft className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <a 
                          href={`/dashboard/employees/${emp._id}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg hover:bg-blue-50" 
                          title="View Details (New Tab)"
                        >
                          <ExternalLink className="w-4 h-4 text-blue-600" />
                        </a>
                        <button onClick={() => openViewModal(emp)} className="p-2 rounded-lg hover:bg-gray-100" title="Quick View">
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        <button onClick={() => navigate(`/dashboard/employees/${emp._id}/edit`)} className="p-2 rounded-lg hover:bg-gray-100" title="Edit">
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        {emp._id !== user._id && (
                          <button onClick={() => openDeleteModal(emp)} className="p-2 rounded-lg hover:bg-gray-100" title="Delete">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {usersData?.pagination?.pages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <Pagination
                currentPage={usersData.pagination.page}
                totalPages={usersData.pagination.pages}
                onPageChange={(page) => {
                  const params = { page };
                  if (search) params.search = search;
                  if (activeFilter !== undefined) params.active = activeFilter;
                  refetchUsers(params);
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-1">No employees found</h3>
          <p className="text-gray-500 mb-4">
            {search ? `No results for "${search}"` : 'Start by adding your first employee'}
          </p>
          {search ? (
            <Button variant="outline" onClick={() => setSearch('')}>Clear Search</Button>
          ) : (
            <Button onClick={() => navigate('/dashboard/employees/add')}>Add Employee</Button>
          )}
        </div>
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Employee" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Full Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Input
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <Input
            label="Password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Role *"
              value={form.role}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              <option value="">Select Role</option>
              <option value="employee">Employee</option>
              <option value="marketing">Marketing Executive</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </Select>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                type="text"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                className="form-input w-full bg-gray-50"
                placeholder="Auto-filled based on role"
                readOnly
              />
              <p className="text-xs text-gray-400 mt-1">Auto-assigned based on role</p>
            </div>
          </div>

          {/* Permissions Section - shown for non-admin roles */}
          {form.role && form.role !== 'admin' && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-5 h-5 text-purple-600" />
                <h4 className="font-medium text-gray-900">Special Permissions</h4>
              </div>
              <p className="text-xs text-gray-500 mb-3">Grant this employee additional permissions beyond their role</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                  form.permissions?.canGeneratePayroll ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="checkbox"
                    checked={form.permissions?.canGeneratePayroll || false}
                    onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, canGeneratePayroll: e.target.checked } })}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Generate Payroll</p>
                    <p className="text-xs text-gray-500">Can generate & manage payslips for employees</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                  form.permissions?.canGenerateLetters ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="checkbox"
                    checked={form.permissions?.canGenerateLetters || false}
                    onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, canGenerateLetters: e.target.checked } })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Generate Letters</p>
                    <p className="text-xs text-gray-500">Can generate & upload letters for employees</p>
                  </div>
                </label>
              </div>
            </div>
          )}
          
          {/* Office Location Assignment */}
          {officeLocations.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-900">Location Tracking Settings</h4>
              </div>
              
              {/* Location Type Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Work Location Type *</label>
                <div className="grid grid-cols-2 gap-3">
                  <label 
                    className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      form.locationType === 'office' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="locationType"
                      value="office"
                      checked={form.locationType === 'office'}
                      onChange={(e) => setForm({ ...form, locationType: e.target.value })}
                      className="sr-only"
                    />
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                      form.locationType === 'office' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-6 h-6 ${form.locationType === 'office' ? 'text-blue-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <span className={`font-medium ${form.locationType === 'office' ? 'text-blue-700' : 'text-gray-700'}`}>
                      Office Based
                    </span>
                    <span className="text-xs text-gray-500 text-center mt-1">
                      Developers, Accountants, etc.<br/>Must work from office location
                    </span>
                  </label>
                  
                  <label 
                    className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      form.locationType === 'field' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="locationType"
                      value="field"
                      checked={form.locationType === 'field'}
                      onChange={(e) => setForm({ ...form, locationType: e.target.value })}
                      className="sr-only"
                    />
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                      form.locationType === 'field' ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-6 h-6 ${form.locationType === 'field' ? 'text-green-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <span className={`font-medium ${form.locationType === 'field' ? 'text-green-700' : 'text-gray-700'}`}>
                      Field / Traveling
                    </span>
                    <span className="text-xs text-gray-500 text-center mt-1">
                      Marketing, Sales, etc.<br/>Can work from anywhere, tracked
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Office location assignment - only for office-based employees */}
              {form.locationType === 'office' && (
                <>
                  <p className="text-sm text-gray-500 mb-3">
                    Select which office locations this employee can punch in/out from
                  </p>
                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.allowAllOfficeLocations}
                      onChange={(e) => setForm({ 
                        ...form, 
                        allowAllOfficeLocations: e.target.checked,
                        assignedOfficeLocations: e.target.checked ? [] : form.assignedOfficeLocations
                      })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Allow access to all office locations</span>
                  </label>
                  {!form.allowAllOfficeLocations && (
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                      {officeLocations.map((loc) => (
                        <label key={loc._id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.assignedOfficeLocations.includes(loc._id)}
                            onChange={(e) => {
                              const newAssigned = e.target.checked
                                ? [...form.assignedOfficeLocations, loc._id]
                                : form.assignedOfficeLocations.filter(id => id !== loc._id);
                              setForm({ ...form, assignedOfficeLocations: newAssigned });
                            }}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700">{loc.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {/* Info for field employees */}
              {form.locationType === 'field' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>📍 Field Employee Tracking:</strong> This employee's location will be tracked every hour. 
                    You can view their travel path on a map in the Employee Details page.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createLoading}>Create Employee</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Employee" size="lg">
        <form onSubmit={handleEdit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Full Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              disabled
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Input
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <Input
            label="New Password (leave blank to keep current)"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Role *"
              value={form.role}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              <option value="">Select Role</option>
              <option value="employee">Employee</option>
              <option value="marketing">Marketing Executive</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </Select>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                type="text"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                className="form-input w-full bg-gray-50"
                placeholder="Auto-filled based on role"
                readOnly
              />
              <p className="text-xs text-gray-400 mt-1">Auto-assigned based on role</p>
            </div>
          </div>

          {/* Permissions Section - shown for non-admin roles */}
          {form.role && form.role !== 'admin' && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-5 h-5 text-purple-600" />
                <h4 className="font-medium text-gray-900">Special Permissions</h4>
              </div>
              <p className="text-xs text-gray-500 mb-3">Grant this employee additional permissions beyond their role</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                  form.permissions?.canGeneratePayroll ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="checkbox"
                    checked={form.permissions?.canGeneratePayroll || false}
                    onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, canGeneratePayroll: e.target.checked } })}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Generate Payroll</p>
                    <p className="text-xs text-gray-500">Can generate & manage payslips for employees</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                  form.permissions?.canGenerateLetters ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="checkbox"
                    checked={form.permissions?.canGenerateLetters || false}
                    onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, canGenerateLetters: e.target.checked } })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Generate Letters</p>
                    <p className="text-xs text-gray-500">Can generate & upload letters for employees</p>
                  </div>
                </label>
              </div>
            </div>
          )}
          
          {/* Office Location Assignment */}
          {officeLocations.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-900">Location Tracking Settings</h4>
              </div>
              
              {/* Location Type Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Work Location Type *</label>
                <div className="grid grid-cols-2 gap-3">
                  <label 
                    className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      form.locationType === 'office' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="editLocationType"
                      value="office"
                      checked={form.locationType === 'office'}
                      onChange={(e) => setForm({ ...form, locationType: e.target.value })}
                      className="sr-only"
                    />
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                      form.locationType === 'office' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-6 h-6 ${form.locationType === 'office' ? 'text-blue-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <span className={`font-medium ${form.locationType === 'office' ? 'text-blue-700' : 'text-gray-700'}`}>
                      Office Based
                    </span>
                    <span className="text-xs text-gray-500 text-center mt-1">
                      Must work from office
                    </span>
                  </label>
                  
                  <label 
                    className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      form.locationType === 'field' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="editLocationType"
                      value="field"
                      checked={form.locationType === 'field'}
                      onChange={(e) => setForm({ ...form, locationType: e.target.value })}
                      className="sr-only"
                    />
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                      form.locationType === 'field' ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-6 h-6 ${form.locationType === 'field' ? 'text-green-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <span className={`font-medium ${form.locationType === 'field' ? 'text-green-700' : 'text-gray-700'}`}>
                      Field / Traveling
                    </span>
                    <span className="text-xs text-gray-500 text-center mt-1">
                      Can work from anywhere
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Office location assignment - only for office-based employees */}
              {form.locationType === 'office' && (
                <>
                  <p className="text-sm text-gray-500 mb-3">
                    Select which office locations this employee can punch in/out from
                  </p>
                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.allowAllOfficeLocations}
                      onChange={(e) => setForm({ 
                        ...form, 
                        allowAllOfficeLocations: e.target.checked,
                        assignedOfficeLocations: e.target.checked ? [] : form.assignedOfficeLocations
                      })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Allow access to all office locations</span>
                  </label>
                  {!form.allowAllOfficeLocations && (
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                      {officeLocations.map((loc) => (
                        <label key={loc._id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.assignedOfficeLocations.includes(loc._id)}
                            onChange={(e) => {
                              const newAssigned = e.target.checked
                                ? [...form.assignedOfficeLocations, loc._id]
                                : form.assignedOfficeLocations.filter(id => id !== loc._id);
                              setForm({ ...form, assignedOfficeLocations: newAssigned });
                            }}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700">{loc.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {/* Info for field employees */}
              {form.locationType === 'field' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>📍 Field Employee:</strong> Location tracked hourly. View travel path on map.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={editLoading}>Update Employee</Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Employee Details" size="md">
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
                {selectedUser.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h3 className="text-lg font-semibold">{selectedUser.name}</h3>
                <p className="text-gray-500">@{selectedUser.username}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{selectedUser.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{selectedUser.phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <Badge variant={selectedUser.role === 'admin' ? 'danger' : 'primary'}>
                  {selectedUser.role}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Department</p>
                <p className="font-medium">{selectedUser.department || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge variant={selectedUser.active ? 'success' : 'secondary'}>
                  {selectedUser.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Login</p>
                <p className="font-medium">
                  {selectedUser.lastLogin
                    ? new Date(selectedUser.lastLogin).toLocaleDateString()
                    : 'Never'}
                </p>
              </div>
            </div>
            {/* Office Locations */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                <p className="text-sm text-gray-500">Office Locations</p>
              </div>
              {selectedUser.allowAllOfficeLocations ? (
                <Badge variant="primary">All Locations</Badge>
              ) : selectedUser.assignedOfficeLocations?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedUser.assignedOfficeLocations.map((locId) => {
                    const loc = officeLocations.find(l => l._id === locId);
                    return loc ? (
                      <Badge key={locId} variant="secondary">{loc.name}</Badge>
                    ) : null;
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No locations assigned</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete Employee" size="sm">
        <div className="text-center">
          <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Are you sure you want to delete</p>
          <p className="font-semibold text-gray-900 mb-4">{selectedUser?.name}?</p>
          <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteLoading}>Delete</Button>
          </div>
        </div>
      </Modal>

      {/* Deactivate Employee Modal */}
      <Modal isOpen={isDeactivateModalOpen} onClose={() => setIsDeactivateModalOpen(false)} title="Deactivate Employee" size="md">
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium">
              {selectedUser?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{selectedUser?.name}</p>
              <p className="text-sm text-gray-500">@{selectedUser?.username}</p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              ⚠️ This employee will not be able to login after deactivation. The reason will be shown when they attempt to login.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Deactivation <span className="text-red-500">*</span>
            </label>
            <select
              value={deactivationForm.reason}
              onChange={(e) => setDeactivationForm({ ...deactivationForm, reason: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select reason...</option>
              <option value="terminated">Terminated</option>
              <option value="resignation">Resignation</option>
              <option value="abscond">Abscond</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Effective Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={deactivationForm.date}
              onChange={(e) => setDeactivationForm({ ...deactivationForm, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Details
            </label>
            <textarea
              value={deactivationForm.details}
              onChange={(e) => setDeactivationForm({ ...deactivationForm, details: e.target.value })}
              rows={3}
              placeholder="Enter any additional details about the deactivation..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setIsDeactivateModalOpen(false)}>Cancel</Button>
            <Button 
              variant="danger" 
              onClick={handleDeactivate} 
              loading={toggleLoading}
              disabled={!deactivationForm.reason}
            >
              Deactivate Employee
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Upload Modal */}
      {isBulkUploadOpen && (
        <EmployeeBulkUpload
          onUploadSuccess={() => {
            setIsBulkUploadOpen(false);
            refetchUsers();
            refetchStats();
            setSuccess('Employees imported successfully');
            setTimeout(() => setSuccess(''), 4000);
          }}
          onCancel={() => setIsBulkUploadOpen(false)}
        />
      )}
    </div>
  );
};

export default EmployeesPage;
